import { Controller } from "@hotwired/stimulus"

// Which dishes are on screen, entirely in the page: the card a diner picked, and what they then
// searched or filtered it down to. The menu is one server-rendered document with no Turbo behind
// it and an etag in front of it — every card the restaurant publishes is already on their phone —
// so a round trip per keystroke, or per tap on the dinner menu, would spend the whole point of
// both to answer a question the DOM can answer.
//
// The two halves are one controller because they are one question. The empty state, the sticky
// nav chips and the filter row all depend on the card *and* the filter, and splitting them would
// mean two controllers telling the same elements to hide for different reasons.
//
// The scrollspy is here for the same reason: which chip is highlighted and which chips exist are
// the same question asked of the same elements, and a category a filter has just emptied must not
// be the one the bar says a diner is reading.
//
// It sits on `.menu-page` rather than on the menu section, because the sticky category nav is
// outside it and has to be filtered with it.
export default class extends Controller<HTMLElement> {
  static targets = [
    "toolbar",
    "query",
    "clear",
    "vegetarian",
    "spicy",
    "allergen",
    "item",
    "category",
    "navItem",
    "navList",
    "empty",
    "tab",
  ]

  declare readonly toolbarTarget: HTMLElement
  declare readonly queryTarget: HTMLInputElement
  declare readonly clearTarget: HTMLButtonElement
  declare readonly vegetarianTarget: HTMLButtonElement
  declare readonly hasVegetarianTarget: boolean
  declare readonly spicyTargets: HTMLButtonElement[]
  declare readonly allergenTargets: HTMLButtonElement[]
  declare readonly itemTargets: HTMLElement[]
  declare readonly categoryTargets: HTMLElement[]
  declare readonly navItemTargets: HTMLElement[]
  declare readonly navListTarget: HTMLElement
  declare readonly emptyTarget: HTMLElement
  declare readonly tabTargets: HTMLAnchorElement[]

  // Normalised once on connect rather than on every keystroke: the haystack never changes, and
  // `normalize()` over a few hundred descriptions is not something to redo per typed character.
  haystacks = new Map<HTMLElement, string>()
  // Split once for the same reason, and read on every keystroke by every excluded allergen.
  allergens = new Map<HTMLElement, string[]>()
  // Which card each dish belongs to, read off the category it sits in.
  menus = new Map<HTMLElement, string | undefined>()
  currentMenu: string | null = null
  vegetarianOnly = false
  spicyLevel: string | null = null
  // Several at once, unlike the spice level: two allergies is not an unusual number to have.
  excludedAllergens = new Set<string>()
  // Which chip each section of the page belongs to, and which of those sections are under the nav.
  chips = new Map<HTMLElement, HTMLElement>()
  onScreen = new Set<HTMLElement>()
  currentChip: HTMLElement | null = null
  observer: IntersectionObserver | null = null

  connect() {
    this.itemTargets.forEach((item) => {
      this.haystacks.set(item, normalize(item.dataset.search ?? ""))
      this.allergens.set(item, (item.dataset.allergens ?? "").split(" ").filter(Boolean))
      this.menus.set(item, item.closest<HTMLElement>("[data-menu]")?.dataset.menu)
    })

    this.currentMenu = this.tabTargets.find((tab) => tab.getAttribute("aria-current") !== null)?.dataset.menu ?? null

    this.#drawChips()
    // Nothing to search on a page that holds no dish.
    this.toolbarTarget.hidden = this.itemTargets.length === 0
    // The transitions belong to a filter that runs, not to the document the server sent.
    this.element.classList.add("menu-page--filterable")

    this.#watchSections()
  }

  disconnect() {
    this.observer?.disconnect()
    this.onScreen.clear()
  }

  apply() {
    const query = normalize(this.queryTarget.value)
    let matches = 0

    this.itemTargets.forEach((item) => {
      const kept = this.#matches(item, query)

      item.hidden = !kept

      if (kept) matches += 1
    })

    // A category whose every dish was filtered out keeps nothing but its heading, so it goes with
    // them — and so does the nav chip that jumps to it, which would otherwise scroll to nowhere.
    // A card nobody is looking at goes the same way, every one of its dishes having been rejected.
    this.categoryTargets.forEach((category) => {
      const kept = this.itemTargets.some((item) => !item.hidden && category.contains(item))

      category.hidden = !kept
      this.#navItemFor(category)?.toggleAttribute("hidden", !kept)
    })

    this.clearTarget.hidden = this.queryTarget.value.length === 0
    this.emptyTarget.hidden = matches > 0
  }

  // The card a diner asked for. The tabs are real links to the same page, so this is the whole of
  // what the server would have answered — minus the round trip, and minus losing what they typed.
  switchMenu(event: Event) {
    event.preventDefault()

    const tab = event.currentTarget as HTMLAnchorElement

    this.currentMenu = tab.dataset.menu ?? null

    this.tabTargets.forEach((target) => {
      const current = target === tab

      target.classList.toggle("menu-chip--accent", current)

      if (current) {
        target.setAttribute("aria-current", "page")
      } else {
        target.removeAttribute("aria-current")
      }
    })

    // The search survives the switch — "where is the chicken" is a fair question to ask of the
    // next card — but the chips do not: one left pressed on a chip the new card does not draw is
    // state a diner can no longer see, let alone undo.
    this.#releaseChips()
    this.#drawChips()
    // So that what is on screen is what the address bar says, and what gets shared.
    history.replaceState({}, "", tab.href)

    this.apply()
  }

  clear() {
    this.queryTarget.value = ""
    this.apply()
    this.queryTarget.focus()
  }

  toggleVegetarian() {
    this.vegetarianOnly = !this.vegetarianOnly
    this.vegetarianTarget.setAttribute("aria-pressed", String(this.vegetarianOnly))
    this.vegetarianTarget.classList.toggle("menu-chip--accent", this.vegetarianOnly)

    this.apply()
  }

  // One level at a time, and the pressed chip clears itself.
  toggleSpicy(event: Event) {
    const chip = event.currentTarget as HTMLButtonElement

    this.spicyLevel = this.spicyLevel === chip.dataset.level ? null : (chip.dataset.level ?? null)

    this.spicyTargets.forEach((target) => {
      const pressed = target.dataset.level === this.spicyLevel

      target.setAttribute("aria-pressed", String(pressed))
      target.classList.toggle("menu-chip--accent", pressed)
    })

    this.apply()
  }

  // Hides the dishes that *declare* the allergen, which is all the card knows: one that declares
  // nothing is undeclared, not free of it, and hiding those too would empty a card whose owner has
  // only filled in half of them while promising something no menu can promise.
  toggleAllergen(event: Event) {
    const chip = event.currentTarget as HTMLButtonElement
    const allergen = chip.dataset.allergen

    if (allergen === undefined) return

    const pressed = !this.excludedAllergens.has(allergen)

    if (pressed) {
      this.excludedAllergens.add(allergen)
    } else {
      this.excludedAllergens.delete(allergen)
    }

    chip.setAttribute("aria-pressed", String(pressed))
    chip.classList.toggle("menu-chip--accent", pressed)

    this.apply()
  }

  #matches(item: HTMLElement, query: string): boolean {
    if (this.currentMenu !== null && this.menus.get(item) !== this.currentMenu) return false

    if (this.vegetarianOnly && item.dataset.vegetarian !== "true") return false

    if (this.spicyLevel !== null && item.dataset.spicy !== this.spicyLevel) return false

    if (this.#declaresExcludedAllergen(item)) return false

    return query.length === 0 || (this.haystacks.get(item) ?? "").includes(query)
  }

  #declaresExcludedAllergen(item: HTMLElement): boolean {
    if (this.excludedAllergens.size === 0) return false

    return (this.allergens.get(item) ?? []).some((allergen) => this.excludedAllergens.has(allergen))
  }

  #navItemFor(category: HTMLElement): HTMLElement | undefined {
    return this.navItemTargets.find((navItem) => navItem.dataset.category === category.id)
  }

  // Scrollspy: a diner who has scrolled down to the desserts reads a bar of identical links and
  // has to work out where they are in a card they have never seen. Each chip already names what it
  // points at in its own `href`, so the section it belongs to is read off that rather than declared
  // a second time.
  //
  // Observed rather than measured on scroll: the browser answers "is this section under the nav"
  // without a listener, and a category a filter has hidden has no box at all — so it stops
  // intersecting on its own and the highlight follows the filter without either half knowing about
  // the other.
  #watchSections() {
    this.navItemTargets.forEach((navItem) => {
      const hash = navItem.querySelector<HTMLAnchorElement>("a[href^='#']")?.hash
      const section = hash ? document.querySelector<HTMLElement>(hash) : null

      if (section) this.chips.set(section, navItem)
    })

    // A band across the top quarter of what is readable, rather than the whole viewport: a category
    // fills more than a screen, so several are in view at once and the one crossing the top of the
    // reading area is the one being read. Measured off the bar itself, which is what it has to clear.
    const bar = Math.round(this.navListTarget.getBoundingClientRect().height)

    this.observer = new IntersectionObserver((entries) => this.#spy(entries), {
      rootMargin: `-${bar}px 0px -75% 0px`,
    })

    this.chips.forEach((_chip, section) => this.observer?.observe(section))
  }

  #spy(entries: IntersectionObserverEntry[]) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        this.onScreen.add(entry.target as HTMLElement)
      } else {
        this.onScreen.delete(entry.target as HTMLElement)
      }
    })

    // The topmost section in the band. An empty band means a diner is between two of them, or above
    // the first: the one they last passed is still the one they are in, so the highlight stands.
    const [section] = [...this.onScreen].sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)

    if (section) this.#markCurrent(section)
  }

  #markCurrent(section: HTMLElement) {
    const chip = this.chips.get(section)

    if (chip === undefined || chip === this.currentChip) return

    this.currentChip?.querySelector("a")?.removeAttribute("aria-current")
    chip.querySelector("a")?.setAttribute("aria-current", "location")
    this.currentChip = chip

    // The bar scrolls sideways on a phone, where the chip just highlighted is as likely to be off
    // the edge of the screen as not. No `behavior`, so the SCSS decides how it moves — and
    // `prefers-reduced-motion` turns it off there, as it does for the jump a chip itself makes.
    const list = this.navListTarget
    const offset = chip.getBoundingClientRect().left - list.getBoundingClientRect().left

    list.scrollTo({ left: list.scrollLeft + offset - (list.clientWidth - chip.clientWidth) / 2 })
  }

  // Everything the toolbar draws for some cards and not others — the chips, and the sentence
  // qualifying the declarations they filter on — says which cards in `data-menus`. One rule for
  // all of them rather than a target apiece: what they have in common is the attribute.
  #drawChips() {
    this.element.querySelectorAll<HTMLElement>("[data-menus]").forEach((chip) => {
      const menus = (chip.dataset.menus ?? "").split(" ").filter(Boolean)

      chip.hidden = this.currentMenu !== null && !menus.includes(this.currentMenu)
    })
  }

  #releaseChips() {
    this.vegetarianOnly = false
    this.spicyLevel = null
    this.excludedAllergens.clear()

    const chips = [...this.spicyTargets, ...this.allergenTargets]

    if (this.hasVegetarianTarget) chips.push(this.vegetarianTarget)

    chips.forEach((chip) => {
      chip.setAttribute("aria-pressed", "false")
      chip.classList.remove("menu-chip--accent")
    })
  }
}

// Case- and accent-insensitive, because a diner types "poelee" and the card says « poêlée » — and
// on a phone keyboard they are not going looking for the circumflex.
function normalize(value: string): string {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .trim()
}
